import { useState, useRef, useEffect, useCallback } from 'react';
import { X, MoveHorizontal, Maximize2, GitBranch } from 'lucide-react';
import { HudBadge } from '../hud';

const fullImg = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

/**
 * BeforeAfterCompareDialog — interactive split-view comparator between a
 * validated parent render (left) and a variation child render (right).
 *
 * Pattern: a single container hosts both images stacked at full size.
 * The TOP image (variation) is clipped via inline `clipPath: inset(0 X% 0 0)`
 * where X% is driven by the slider. A vertical handle marks the seam.
 *
 * Props:
 *   parent     ({ image_url, ... })   — left side (validated render)
 *   variation  ({ image_url, variation_category, variation_value, variation_note, completed_at })
 *   onClose()
 */
const BeforeAfterCompareDialog = ({ parent, variation, onClose }) => {
  const [pct, setPct] = useState(50); // 0–100 (% of width revealed of the BEFORE)
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  // ESC closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Pointer-anywhere dragging (mouse + touch via Pointer Events)
  const moveTo = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    setPct(Math.round((x / rect.width) * 100));
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    moveTo(e.clientX);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => moveTo(e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging, moveTo]);

  if (!parent?.image_url || !variation?.image_url) return null;

  // Variation chip label (mirrors the gallery formatting)
  const VARIATION_LABELS = {
    time_of_day: { _: 'Heure', morning: 'Matin', midday: 'Midi', golden_hour: 'Golden hour', blue_hour: 'Blue hour', night: 'Nuit' },
    weather:     { _: 'Météo', clear: 'Ciel clair', cloudy: 'Nuageux', rain: 'Pluie', snow: 'Neige', fog: 'Brouillard' },
    season:      { _: 'Saison', spring: 'Printemps', summer: 'Été', autumn: 'Automne', winter: 'Hiver' },
    lighting:    { _: 'Éclairage', natural: 'Naturel', dim: 'Tamisé', warm: 'Chaud', cool: 'Froid', festive: 'Festif' },
    material:    { _: 'Matériau', sol: 'Sol', mur: 'Murs', plafond: 'Plafond', mobilier: 'Mobilier', cuisine: 'Cuisine', sdb: 'SDB' },
  };
  const cat = VARIATION_LABELS[variation.variation_category] || {};
  const varLabel = variation.variation_category
    ? `${cat._ || variation.variation_category} · ${cat[variation.variation_value] || variation.variation_value}`
    : 'Variation';

  return (
    <div
      className="fixed inset-0 z-[250] flex items-stretch sm:items-center justify-center p-0 sm:p-4"
      data-testid="before-after-dialog"
      style={{ background: 'rgba(6,8,12,0.92)', backdropFilter: 'blur(14px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-6xl rounded-none sm:rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: 'var(--c-surface-1)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)',
          maxHeight: '100vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <div className="flex items-center gap-3">
            <span className="hud-eyebrow hud-eyebrow-gold">Comparateur</span>
            <span
              className="hud-badge"
              style={{
                background: 'rgba(34,197,94,0.18)', color: '#86EFAC',
                border: '1px solid rgba(34,197,94,0.35)', fontSize: 10, padding: '2px 8px',
              }}
              data-testid="before-after-parent-chip"
            >
              ✓ Avant — Rendu validé
            </span>
            <MoveHorizontal className="w-4 h-4" style={{ color: 'var(--c-text-muted)' }} />
            <span
              className="hud-badge"
              style={{
                background: 'rgba(147,197,253,0.18)', color: '#93C5FD',
                border: '1px solid rgba(147,197,253,0.35)', fontSize: 10, padding: '2px 8px',
              }}
              data-testid="before-after-variation-chip"
            >
              <GitBranch className="w-2.5 h-2.5" />
              Après — {varLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={fullImg(variation.image_url)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="before-after-open-fullsize"
              className="rounded-md p-2 transition-colors"
              style={{ border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-gold)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-2)'; }}
              title="Ouvrir la variation en pleine taille"
            >
              <Maximize2 className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={onClose}
              data-testid="before-after-close"
              className="rounded-md p-2 transition-colors"
              style={{ border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-gold)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-2)'; }}
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body — comparator */}
        <div
          ref={containerRef}
          onPointerDown={onPointerDown}
          data-testid="before-after-canvas"
          className="relative flex-1 select-none"
          style={{
            background: '#06080c',
            minHeight: 360,
            cursor: dragging ? 'grabbing' : 'ew-resize',
            touchAction: 'none',
          }}
        >
          {/* BEFORE (parent) — bottom layer, full width */}
          <img
            src={fullImg(parent.image_url)}
            alt="Rendu validé (avant)"
            draggable={false}
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: 'contain' }}
          />
          {/* AFTER (variation) — clipped from the left, only the right portion is visible */}
          <img
            src={fullImg(variation.image_url)}
            alt="Variation (après)"
            draggable={false}
            data-testid="before-after-after-image"
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              objectFit: 'contain',
              clipPath: `inset(0 0 0 ${pct}%)`,
              WebkitClipPath: `inset(0 0 0 ${pct}%)`,
              transition: dragging ? 'none' : 'clip-path 0.05s linear',
            }}
          />
          {/* Vertical handle */}
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
          >
            <div
              className="absolute top-0 bottom-0 w-px"
              style={{
                background: 'linear-gradient(180deg, transparent 0%, rgba(217,160,91,0.85) 12%, rgba(217,160,91,0.85) 88%, transparent 100%)',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
              }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 flex items-center justify-center rounded-full"
              style={{
                width: 36, height: 36,
                background: 'rgba(8,10,14,0.92)',
                border: '1px solid var(--c-border-accent)',
                color: 'var(--c-gold)',
                boxShadow: '0 6px 18px -4px rgba(0,0,0,0.6)',
                pointerEvents: 'auto',
                cursor: dragging ? 'grabbing' : 'grab',
              }}
              data-testid="before-after-handle"
            >
              <MoveHorizontal className="w-4 h-4" />
            </div>
          </div>
          {/* Corner labels — small persistent reminders */}
          <div
            className="absolute top-3 left-3 px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.14em] font-semibold"
            style={{ background: 'rgba(8,10,14,0.7)', color: '#86EFAC', border: '1px solid rgba(34,197,94,0.35)' }}
          >
            AVANT
          </div>
          <div
            className="absolute top-3 right-3 px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.14em] font-semibold"
            style={{ background: 'rgba(8,10,14,0.7)', color: '#93C5FD', border: '1px solid rgba(147,197,253,0.35)' }}
          >
            APRÈS
          </div>
        </div>

        {/* Footer — slider explicit + metadata */}
        <div
          className="flex items-center justify-between gap-4 px-5 py-3"
          style={{ borderTop: '1px solid var(--c-border)' }}
        >
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: '#86EFAC' }}>Avant</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={pct}
              onChange={(e) => setPct(Number(e.target.value))}
              data-testid="before-after-slider"
              className="flex-1 accent-amber-500"
              aria-label="Position du comparateur"
            />
            <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: '#93C5FD' }}>Après</span>
          </div>
          {variation.variation_note && (
            <div
              className="text-[11px] max-w-md truncate"
              style={{ color: 'var(--c-text-muted)' }}
              title={variation.variation_note}
            >
              Note : {variation.variation_note}
            </div>
          )}
          {variation.completed_at && (
            <div className="text-[11px]" style={{ color: 'var(--c-text-muted)' }}>
              Généré le {formatDate(variation.completed_at)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterCompareDialog;
